/**
 * Error Log viewer page — Admin only.
 *
 * Searchable/filterable error log viewer.
 * Filters: date range, error code, user, entity type, error reference ID.
 *
 * Requirements: 50.4, 50.5
 */

import { useState, useEffect, useCallback } from 'react';
import { adminService, ErrorLogEntry, ErrorLogFilter } from '../../services/adminService';

export function ErrorLog() {
  const [entries, setEntries] = useState<ErrorLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Filter state
  const [filter, setFilter] = useState<ErrorLogFilter>({
    limit: 50,
    offset: 0,
  });
  const [searchInput, setSearchInput] = useState('');

  const loadErrors = useCallback(async () => {
    try {
      setLoading(true);
      const result = await adminService.getErrors(filter);
      setEntries(result.entries);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load error log.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadErrors();
  }, [loadErrors]);

  const applySearch = () => {
    setFilter((prev) => ({ ...prev, search: searchInput || undefined, offset: 0 }));
  };

  const nextPage = () => {
    setFilter((prev) => ({ ...prev, offset: (prev.offset ?? 0) + (prev.limit ?? 50) }));
  };

  const prevPage = () => {
    setFilter((prev) => ({
      ...prev,
      offset: Math.max(0, (prev.offset ?? 0) - (prev.limit ?? 50)),
    }));
  };

  const formatTime = (iso: string) => new Date(iso).toLocaleString();

  return (
    <div >
      <h1 className="text-lg font-semibold text-text-primary mb-2">Error Log</h1>
      <p className="text-sm text-slate-500 mb-6">
        Technical error log for troubleshooting. Separate from the audit log.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-6 py-4 text-red-700 text-sm mb-5" role="alert">
          {error}
          <button className="ml-4 underline" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <input
          type="text"
          placeholder="Search errors..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && applySearch()}
          className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40 w-64"
          aria-label="Search errors"
        />
        <input
          type="date"
          value={filter.dateFrom ?? ''}
          onChange={(e) => setFilter((prev) => ({ ...prev, dateFrom: e.target.value || undefined, offset: 0 }))}
          className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
          aria-label="Date from"
        />
        <input
          type="date"
          value={filter.dateTo ?? ''}
          onChange={(e) => setFilter((prev) => ({ ...prev, dateTo: e.target.value || undefined, offset: 0 }))}
          className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
          aria-label="Date to"
        />
        <input
          type="text"
          placeholder="Error code"
          value={filter.errorCode ?? ''}
          onChange={(e) => setFilter((prev) => ({ ...prev, errorCode: e.target.value || undefined, offset: 0 }))}
          className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40 w-32"
          aria-label="Error code filter"
        />
        <button
          className="px-4 py-2 bg-baker-red text-white rounded-lg text-sm font-medium hover:bg-baker-red/90"
          onClick={applySearch}
        >
          Search
        </button>
      </div>

      {/* Results */}
      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <div className="text-sm text-slate-500 mb-3">{total} error(s) found</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left bg-page">
                  <th className="py-2 px-3">ID</th>
                  <th className="py-2 px-3">Time</th>
                  <th className="py-2 px-3">Code</th>
                  <th className="py-2 px-3">Message</th>
                  <th className="py-2 px-3">User</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <>
                    <tr
                      key={entry.errorId}
                      className="border-b border-border hover:bg-page/50 cursor-pointer"
                      onClick={() => setExpandedId(expandedId === entry.errorId ? null : entry.errorId)}
                    >
                      <td className="py-2 px-3 font-mono">{entry.errorId}</td>
                      <td className="py-2 px-3">{formatTime(entry.timestamp)}</td>
                      <td className="py-2 px-3">
                        <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs">
                          {entry.errorCode ?? 'UNKNOWN'}
                        </span>
                      </td>
                      <td className="py-2 px-3 truncate max-w-md">{entry.message ?? '—'}</td>
                      <td className="py-2 px-3">{entry.userId ?? '—'}</td>
                    </tr>
                    {expandedId === entry.errorId && (
                      <tr key={`${entry.errorId}-detail`} className="bg-page/50">
                        <td colSpan={5} className="p-4">
                          {entry.stackTrace && (
                            <div className="mb-3">
                              <div className="text-xs font-medium text-slate-500 mb-1">Stack Trace</div>
                              <pre className="text-xs bg-page p-3 rounded-lg overflow-x-auto whitespace-pre-wrap">
                                {entry.stackTrace}
                              </pre>
                            </div>
                          )}
                          {entry.requestContext && (
                            <div>
                              <div className="text-xs font-medium text-slate-500 mb-1">Request Context</div>
                              <pre className="text-xs bg-page p-3 rounded-lg overflow-x-auto">
                                {entry.requestContext}
                              </pre>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>

          {entries.length === 0 && (
            <p className="text-slate-400 text-center py-8">No errors found matching your filters.</p>
          )}

          {/* Pagination */}
          {total > (filter.limit ?? 50) && (
            <div className="flex items-center justify-between mt-4">
              <button
                className="px-4 py-2 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 hover:bg-page disabled:opacity-50"
                onClick={prevPage}
                disabled={(filter.offset ?? 0) === 0}
              >
                Previous
              </button>
              <span className="text-sm text-slate-500">
                Showing {(filter.offset ?? 0) + 1}–{Math.min((filter.offset ?? 0) + (filter.limit ?? 50), total)} of {total}
              </span>
              <button
                className="px-4 py-2 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 hover:bg-page disabled:opacity-50"
                onClick={nextPage}
                disabled={(filter.offset ?? 0) + (filter.limit ?? 50) >= total}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
